import { WidgetState, WidgetConfig, ChatMessage, ChatFileDownload, FileAttachment, AuthState, FileUploadState, McpToolDef, ToolCallState, PendingConfirmation, DrivingModeState } from '../types';
import {
  DEFAULT_CONFIG,
  INITIAL_STATE,
  generateClientChatId,
  readPersistedDrivingModeActive,
  persistDrivingModeActive,
  readPersistedAudioOutputDeviceId,
  persistAudioOutputDeviceId,
  readPersistedCueSoundsEnabled,
  persistCueSoundsEnabled,
} from './defaults';

type Listener = () => void;

function areConfigValuesEqual(
  current: WidgetConfig[keyof WidgetConfig],
  next: WidgetConfig[keyof WidgetConfig],
): boolean {
  if (Object.is(current, next)) return true;

  if (Array.isArray(current) && Array.isArray(next)) {
    return current.length === next.length
      && current.every((value, index) => Object.is(value, next[index]));
  }

  if (
    current
    && next
    && typeof current === 'object'
    && typeof next === 'object'
  ) {
    const currentRecord = current as Record<string, unknown>;
    const nextRecord = next as Record<string, unknown>;
    const currentKeys = Object.keys(currentRecord);
    const nextKeys = Object.keys(nextRecord);

    return currentKeys.length === nextKeys.length
      && currentKeys.every((key) => Object.is(currentRecord[key], nextRecord[key]));
  }

  return false;
}

function hasConfigChanges(
  currentConfig: WidgetConfig,
  partialConfig: Partial<WidgetConfig>,
): boolean {
  return (Object.keys(partialConfig) as Array<keyof WidgetConfig>).some((key) => (
    !areConfigValuesEqual(
      currentConfig[key],
      partialConfig[key] as WidgetConfig[keyof WidgetConfig],
    )
  ));
}

type ZustandMirror = (partial: Partial<WidgetState>) => void;

class WidgetStore {
  private state: WidgetState = { ...INITIAL_STATE, config: { ...DEFAULT_CONFIG }, clientChatId: generateClientChatId() };
  private listeners: Set<Listener> = new Set();
  private _pendingFocus = false;
  private zustandMirror: ZustandMirror | null = null;

  getState(): Readonly<WidgetState> {
    return this.state;
  }

  /** Keeps the React zustand store in sync when orchestrator writes directly to the global store. */
  attachZustandMirror(mirror: ZustandMirror | null): void {
    this.zustandMirror = mirror;
  }

  setState(partial: Partial<WidgetState>): void {
    this.state = { ...this.state, ...partial };
    this.zustandMirror?.(partial);
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  }

  private notify(): void {
    this.listeners.forEach(fn => fn());
  }

  setConfig(config: Partial<WidgetConfig>): void {
    if (!hasConfigChanges(this.state.config, config)) return;
    this.setState({ config: { ...this.state.config, ...config } });
  }

  toggleOpen(): void {
    this.setState({ isOpen: !this.state.isOpen });
  }

  toggleExpanded(): void {
    this.setState({ isExpanded: !this.state.isExpanded });
  }

  addMessage(msg: ChatMessage): void {
    this.setState({ messages: [...this.state.messages, msg] });
  }

  removeMessage(id: string): void {
    this.setState({ messages: this.state.messages.filter(m => m.id !== id) });
  }

  updateMessage(id: string, partial: Partial<ChatMessage>): void {
    this.setState({
      messages: this.state.messages.map(m =>
        m.id === id ? { ...m, ...partial } : m
      ),
    });
  }

  setAuth(auth: Partial<AuthState>): void {
    this.setState({ auth: { ...this.state.auth, ...auth } });
  }

  setFileUpload(fileUpload: Partial<FileUploadState>): void {
    this.setState({ fileUpload: { ...this.state.fileUpload, ...fileUpload } });
  }

  addPendingAttachment(attachment: FileAttachment): void {
    this.setState({ pendingAttachments: [...this.state.pendingAttachments, attachment] });
  }

  removePendingAttachment(fileId: string): void {
    this.setState({ pendingAttachments: this.state.pendingAttachments.filter(a => a.fileId !== fileId) });
  }

  clearPendingAttachments(): FileAttachment[] {
    const attachments = [...this.state.pendingAttachments];
    this.setState({ pendingAttachments: [] });
    return attachments;
  }

  setError(error: string | null): void {
    this.setState({ error });
  }

  setMcpSession(sessionId: string | null): void {
    this.setState({ mcpSessionId: sessionId });
  }

  setTools(tools: McpToolDef[]): void {
    this.setState({ tools });
  }

  addToolCall(tc: ToolCallState): void {
    this.setState({ toolCalls: [...this.state.toolCalls, tc] });
  }

  updateToolCall(id: string, partial: Partial<ToolCallState>): void {
    this.setState({
      toolCalls: this.state.toolCalls.map(tc =>
        tc.id === id ? { ...tc, ...partial } : tc
      ),
    });
  }

  clearToolCalls(): void {
    this.setState({ toolCalls: [] });
  }

  setPendingConfirmation(pc: PendingConfirmation | null): void {
    this.setState({ pendingConfirmation: pc });
  }

  setMessageFeedback(messageId: string, rating: 'positive' | 'negative'): void {
    this.setState({
      messages: this.state.messages.map(m =>
        m.id === messageId ? { ...m, feedback: rating } : m
      ),
    });
  }

  addFileToMessage(messageId: string, file: ChatFileDownload): void {
    this.setState({
      messages: this.state.messages.map(m => {
        if (m.id !== messageId) return m;
        return { ...m, files: [...(m.files || []), file] };
      }),
    });
  }

  setDrivingMode(partial: Partial<DrivingModeState>): void {
    const currentDrivingMode = this.state.drivingMode;
    const nextDrivingMode = { ...currentDrivingMode, ...partial };
    const changed = Object.keys(partial).some((key) => (
      currentDrivingMode[key as keyof DrivingModeState]
      !== nextDrivingMode[key as keyof DrivingModeState]
    ));

    if (!changed) return;

    persistDrivingModeActive(nextDrivingMode.active);
    persistAudioOutputDeviceId(nextDrivingMode.audioOutputDeviceId);
    persistCueSoundsEnabled(nextDrivingMode.drivingCueSoundsEnabled);
    this.setState({ drivingMode: nextDrivingMode });
  }

  requestInputFocus(): void {
    this._pendingFocus = true;
  }

  consumeInputFocus(): boolean {
    const val = this._pendingFocus;
    this._pendingFocus = false;
    return val;
  }

  reset(): void {
    this.state = {
      ...INITIAL_STATE,
      config: { ...DEFAULT_CONFIG },
      clientChatId: generateClientChatId(),
      drivingMode: {
        ...INITIAL_STATE.drivingMode,
        active: readPersistedDrivingModeActive(),
        audioOutputDeviceId: readPersistedAudioOutputDeviceId(),
        drivingCueSoundsEnabled: readPersistedCueSoundsEnabled(),
      },
    };
    this.notify();
  }
}

export const store = new WidgetStore();
